"use client";

import { useState } from "react";
import { ExternalLink, Lock, RefreshCw, Tag, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { groupShoppingItems } from "@/lib/shopping-list";
import { formatGBP } from "@/lib/utils";
import type { Product } from "@/types";
import type { GeneratedDesign } from "@/lib/pipeline/engine";

export function ShoppingList({ items, isUnlocked, onSwapItem }: {
  items: GeneratedDesign["items"];
  isUnlocked: boolean;
  onSwapItem: (itemId: string, replacement: Product) => void;
}) {
  const [swapTargetItem, setSwapTargetItem] = useState<GeneratedDesign["items"][number] | null>(null);
  function chooseAlternative(product: Product) {
    if (!swapTargetItem) return;
    onSwapItem(swapTargetItem.id, product);
    setSwapTargetItem(null);
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-ink">
            {isUnlocked ? "Full Shoppable Product Breakdown" : "Shopping List (Preview)"}
          </h3>
          <span className="text-xs text-ink-soft">Prices include VAT</span>
        </div>

        <p className="text-xs text-ink-soft">Order separately from each retailer. Delivery charges are not included. With affiliate cookies enabled, approved links may earn us a commission at no extra cost to you.</p>
        {groupShoppingItems(items).map(group => <section key={group.retailer} className="space-y-3">
          <h4 className="flex items-center justify-between gap-3 font-semibold">
            <span>{group.retailer}</span><span className="font-mono">{formatGBP(group.subtotal)}</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {group.items.map((item) => (
            <div
              key={item.id}
              className={`bg-paper p-4 rounded-xl border transition-all flex items-start gap-3.5 ${
                item.locked
                  ? "border-line/80 bg-paper-deep/40 opacity-90"
                  : "border-line shadow-soft hover:shadow-card"
              }`}
            >
              {item.image_url ? (
                <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-paper-deep flex-shrink-0">
                  <img
                    src={item.image_url || undefined}
                    alt={item.label}
                    className="w-full h-full object-cover"
                  />
                  {item.locked && (
                    <div className="absolute inset-0 bg-ink/40 backdrop-blur-[2px] flex items-center justify-center text-paper">
                      <Lock className="w-5 h-5" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-20 h-20 rounded-lg bg-paper-deep flex items-center justify-center text-ink-soft flex-shrink-0">
                  <Tag className="w-6 h-6" />
                </div>
              )}

              <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
                <div>
                  <div className="flex items-center justify-between text-[11px] text-ink-soft">
                    <span className="font-bold uppercase tracking-wider text-clay">
                      {item.category.replace(/_/g, " ")}
                    </span>
                    <span>{item.retailer}</span>
                  </div>

                  {item.locked ? (
                    <div className="mt-1">
                      <div className="h-4 bg-line/60 rounded w-3/4 animate-pulse mb-1" />
                      <span className="text-[11px] text-ink-soft italic flex items-center gap-1">
                        <Lock className="w-3 h-3 text-clay" />
                        <span>Unlocked via email</span>
                      </span>
                    </div>
                  ) : (
                    <h4 className="text-sm font-semibold text-ink mt-0.5 truncate">
                      {item.product_title || item.label}
                    </h4>
                  )}
                </div>

                {item.render_in_image === false && <p className="mt-1 text-xs text-ink-soft">Shopping list only · covered by bed linen in the design</p>}
                {item.category === "paint" && item.product?.volume_litres && <p className="mt-1 text-xs text-ink-soft">{item.product.volume_litres}L tin · check coverage before ordering</p>}
                {item.product?.price_needs_size_check && <p className="mt-1 text-xs text-ink-soft">{item.product.size ? `${item.product.size} selected · ` : ""}confirm size price at retailer</p>}

                <div className="mt-2 pt-2 border-t border-line/60 flex items-center justify-between">
                  <span className="text-sm font-mono font-bold text-brass-deep">
                    {formatGBP(item.price_gbp * (item.quantity ?? 1))}
                    {(item.quantity ?? 1) > 1 && <span className="block text-[11px] font-normal text-ink-soft">{item.quantity} × {formatGBP(item.price_gbp)}</span>}
                  </span>

                  {item.locked ? (
                    <span className="text-[11px] font-medium text-ink-soft">Locked item</span>
                  ) : (
                    <div className="flex items-center gap-3">
                      {item.alternatives && item.alternatives.length > 0 && (
                        <button
                          onClick={() => setSwapTargetItem(item)}
                          className="text-xs font-semibold text-ink-soft hover:text-ink cursor-pointer flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" />
                          <span>Swap</span>
                        </button>
                      )}

                      <a
                        href={item.product ? `/api/out/${encodeURIComponent(item.product.id)}` : item.affiliate_url}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-ink text-paper text-[10px] font-bold px-3 py-1.5 rounded-full hover:bg-ink/90 transition-colors"
                      >
                        <span>Buy Now</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          </div>
        </section>)}
      </div>
      {swapTargetItem && (
        <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-paper rounded-3xl border border-line max-w-lg w-full p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="clay">Swap Recommendation</Badge>
                <h3 className="text-lg font-bold text-ink mt-1">
                  Select alternative {swapTargetItem.category}
                </h3>
              </div>
              <button
                onClick={() => setSwapTargetItem(null)}
                className="w-8 h-8 rounded-full bg-paper-deep hover:bg-line text-ink flex items-center justify-center cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {swapTargetItem.alternatives?.map((alt) => (
                <div
                  key={alt.id}
                  className="p-3.5 rounded-2xl border border-line bg-paper hover:bg-paper-deep flex items-center justify-between gap-3 transition-colors"
                >
                  <img
                    src={alt.image_url || undefined}
                    alt={alt.title}
                    className="w-16 h-16 rounded-xl object-cover bg-paper-deep flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-bold text-ink line-clamp-2">{alt.title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-mono text-xs font-bold text-brass-deep">
                        {formatGBP(alt.price_gbp)}
                      </span>
                      {alt.color && (
                        <span className="text-[10px] text-ink-soft">({alt.color})</span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => chooseAlternative(alt)}
                    className="whitespace-nowrap"
                  >
                    Choose
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
